import { useState, useEffect } from 'react';
import { CreditCard, Building2, Smartphone, ExternalLink, Copy, CheckCircle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { billingAPI } from '../lib/api';
import { toast } from 'sonner';

export default function PaymentsPage() {
  const [invoices, setInvoices] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, configRes, bankRes] = await Promise.all([
        billingAPI.getPayableInvoices(),
        billingAPI.getPaymentConfig(),
        billingAPI.getBankDetails()
      ]);
      
      setInvoices(invoicesRes.data.invoices || []);
      setPaymentConfig(configRes.data);
      setBankDetails(bankRes.data);
    } catch (error) {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handlePayWithRazorpay = async (invoice) => {
    if (!paymentConfig?.razorpay_enabled) {
      toast.error('Online payments are not enabled');
      return;
    }

    setProcessingPayment(invoice.id);
    try {
      const { data: order } = await billingAPI.createRazorpayOrder({ invoice_id: invoice.id });
      
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'VIBE6 DIGITAL LLP',
        description: order.description,
        order_id: order.order_id,
        prefill: {
          name: order.customer_name,
          email: order.customer_email
        },
        theme: {
          color: '#0891b2'
        },
        handler: async function(response) {
          try {
            await billingAPI.verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              invoice_id: invoice.id
            });
            toast.success('Payment successful!');
            fetchData();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error('Failed to initiate payment');
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await billingAPI.downloadInvoicePDF(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice.invoice_number.replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CreditCard className="h-6 w-6" />
          Payments
        </h1>
        <p className="text-muted-foreground">View invoices and make payments</p>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="invoices">Outstanding Invoices</TabsTrigger>
          <TabsTrigger value="payment-methods">Payment Methods</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-4">
          {invoices.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-12">
                <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                <p className="text-lg font-medium">All Paid Up!</p>
                <p className="text-muted-foreground">You have no outstanding invoices</p>
              </CardContent>
            </Card>
          ) : (
            invoices.map(invoice => (
              <Card key={invoice.id}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{invoice.invoice_number}</h3>
                        <Badge variant={invoice.status === 'overdue' ? 'destructive' : 'secondary'}>
                          {invoice.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{invoice.project_display_name}</p>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span>Invoice Date: {invoice.invoice_date?.slice(0, 10)}</span>
                        <span>Due: {invoice.due_date?.slice(0, 10)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Balance Due</p>
                      <p className="text-2xl font-bold text-primary">₹{invoice.balance_due?.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(invoice)}>
                      <Download className="h-4 w-4 mr-1" /> View Invoice
                    </Button>
                    {paymentConfig?.razorpay_enabled && (
                      <Button 
                        size="sm" 
                        onClick={() => handlePayWithRazorpay(invoice)}
                        disabled={processingPayment === invoice.id}
                      >
                        <CreditCard className="h-4 w-4 mr-1" />
                        {processingPayment === invoice.id ? 'Processing...' : 'Pay Online'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="payment-methods" className="space-y-4">
          {/* Bank Transfer */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Bank Transfer (NEFT/RTGS/IMPS)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bankDetails && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Account Name</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{bankDetails.company_name}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.company_name, 'Account name')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bank</p>
                      <p className="font-medium">{bankDetails.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Branch</p>
                      <p className="font-medium">{bankDetails.branch}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Account Number</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono">{bankDetails.account_number}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.account_number, 'Account number')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">IFSC Code</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono">{bankDetails.ifsc}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.ifsc, 'IFSC code')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">SWIFT Code (International)</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono">{bankDetails.swift_code}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.swift_code, 'SWIFT code')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* UPI Payment */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                UPI Payment
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bankDetails && (
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex-1">
                    <div className="mb-4">
                      <p className="text-sm text-muted-foreground">UPI ID</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono text-lg">{bankDetails.upi_id}</p>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(bankDetails.upi_id, 'UPI ID')}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Open any UPI app (Google Pay, PhonePe, Paytm, etc.) and pay using the UPI ID above or scan the QR code.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <img 
                      src={bankDetails.upi_qr_url} 
                      alt="UPI QR Code" 
                      className="w-48 h-48 object-contain"
                    />
                    <p className="text-center text-sm text-muted-foreground mt-2">Scan to Pay</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Online Payment */}
          {paymentConfig?.razorpay_enabled && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Online Payment (Card/UPI/NetBanking)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Pay securely using Credit/Debit Card, UPI, or Net Banking through Razorpay.
                </p>
                <div className="flex gap-2">
                  <img src="https://cdn.razorpay.com/static/assets/logo/payment.svg" alt="Razorpay" className="h-8" />
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
